import NextLink from "common/bit/NextLink"
import { Font } from "components/shopFeatures/wizard/constants"
import styled, { css } from "styled-components"
import { HomePageContainer } from "theme/shared"

export const MOBILE = "@media (max-width: 820px)"

export const ArrowButton = styled.button<{ $direction: "prev" | "next" }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  padding: 0;
  border: none;
  border-radius: 24px;
  background: ${(p) => p.theme.colors.neutral200};
  color: ${(p) => p.theme.colors.darkBlack};
  cursor: pointer;
  transition: background 0.25s ease, color 0.25s ease;
  /* prev points right (rotate the left-pointing chevron), next points left */
  svg {
    transform: ${(p) => (p.$direction === "prev" ? "rotate(180deg)" : "none")};
  }
  &:hover {
    background: ${(p) => p.theme.colors.darkBlack};
    color: ${(p) => p.theme.colors.white};
  }
  &:focus-visible {
    outline: 2px solid ${(p) => p.theme.colors.darkBlack};
    outline-offset: 2px;
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

export const HeaderWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  text-align: center;
`

export const Badge = styled.span`
  font-family: ${Font.MazdaType};
  font-weight: 600;
  font-size: 14px;
  line-height: 24px;
  letter-spacing: 4px;
  text-transform: uppercase;
  color: ${(p) => p.theme.colors.newPrimaryRed};
  min-height: 24px;
  ${MOBILE} {
    min-height: 20px;
    line-height: 20px;
  }
`

export const ModelName = styled.h3`
  margin: 0;
  font-family: ${Font.MazdaType};
  font-weight: 400;
  font-size: 40px;
  line-height: 48px;
  letter-spacing: 4px;
  color: ${(p) => p.theme.colors.darkBlack};
  ${MOBILE} {
    font-size: 24px;
    line-height: 29px;
    letter-spacing: 1.44px;
  }
`

export const Subtitle = styled.span`
  font-family: ${Font.MazdaType};
  font-weight: 400;
  font-size: 18px;
  line-height: 26px;
  letter-spacing: 2px;
  color: ${(p) => p.theme.colors.darkBlack};
  ${MOBILE} {
    font-size: 14px;
    line-height: 18px; /* 128.571% */
    letter-spacing: 0;
  }
`

export const DetailsWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`

export const PriceGrid = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

export const PriceCol = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 110px;
`

export const PriceLabel = styled.span`
  font-family: ${Font.OENaPro};
  font-weight: 300;
  font-size: 14px;
  line-height: 24px;
  letter-spacing: 2px;
  color: ${(p) => p.theme.colors.neutral};
  padding-bottom: 8px;
`

export const PriceNum = styled.div`
  font-family: ${Font.MazdaType};
  font-weight: 300;
  font-size: 32px;
  line-height: 48px;
  letter-spacing: 0.6px;
  color: ${(p) => p.theme.colors.darkBlack};
  white-space: nowrap;
  ${MOBILE} {
    font-size: 20px;
    line-height: 26px;
    letter-spacing: 0.2px;
  }
`

export const Shekel = styled.span`
  font-size: 20px;
`

export const PriceDivider = styled.span`
  width: 1px;
  height: 62px;
  background: ${(p) => p.theme.colors.neutral300};
  transform: rotate(20deg);
`

export const PriceNote = styled.p`
  margin: 0;
  font-family: ${Font.OENaPro};
  font-weight: 300;
  font-size: 16px;
  line-height: 24px;
  color: ${(p) => p.theme.colors.darkBlack};
  text-align: center;
`

export const OnlineLink = styled(NextLink)`
  font-family: ${Font.OENaPro};
  font-weight: 400;
  font-size: 16px;
  line-height: 26px;
  color: ${(p) => p.theme.colors.newPrimaryRed};
  text-decoration: none;
`

export const Cta = styled(NextLink)`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  margin-top: 12px;
  text-decoration: none;
  color: ${(p) => p.theme.colors.darkBlack};
  font-family: ${Font.OENaPro};
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
`

export const CtaCircle = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border: 2px solid ${(p) => p.theme.colors.darkBlack};
  border-radius: 40px;
`

export const SwiperWrap = styled.div`
  width: 100%;
  /* Non-active (side) cars: dimmed, desaturated, shrunk and pushed down so the
     centered active car reads as featured. Mirrors the live reference. */
  .swiper-slide {
    width: 700px;
    max-width: 80vw;
    display: flex;
    justify-content: center;
    opacity: 0.55;
    transform: translateY(-37px) scale(0.62);
    filter: saturate(0.9);
    transition: opacity 0.4s ease, transform 0.4s ease, filter 0.4s ease;
  }
  ${(p) => p.theme.breakpoints.down("smallLaptop")} {
    .swiper-slide {
      max-width: 55vw;
    }
  }
  .swiper-slide-active {
    opacity: 1;
    transform: none;
    filter: none;
  }
  /* Side-car hover: more opaque + a slight grow (reference behaviour). */
  .swiper-slide:not(.swiper-slide-active):hover {
    opacity: 0.85;
    transform: translateY(-37px) scale(0.66);
  }
  ${MOBILE} {
    .swiper {
      overflow: visible; /* allow the peeking neighbours to show outside the container */
    }
    /* Narrower-than-viewport slides so both neighbours peek from the edges.
       Side cars stay full-size (no shrink) on mobile, just dimmed + lowered.
       Narrower active slide (DLK-752) pulls the peeking neighbours closer,
       reducing the empty space between the centered car and its neighbours. */
    .swiper-slide {
      width: 55vw;
      transform: translateY(20px) scale(1.5);
    }
    .swiper-slide:not(.swiper-slide-active) {
      transform: translateY(0px) scale(1);
    }
    .swiper-slide:not(.swiper-slide-active):hover {
      transform: translateY(0px) scale(1);
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .swiper-slide {
      transition: none;
    }
  }
`

export const SlideImage = styled.img`
  /* Block, not inline: an inline img leaves a font-dependent gap below it, which
     differs between the <button> and <a> wrappers and would shift the layout. */
  display: block;
  width: 100%;
  aspect-ratio: 16 / 9;
  object-fit: contain;
`

const slideTarget = css`
  display: block;
  width: 100%;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  &:focus-visible {
    outline: 2px solid ${(p) => p.theme.colors.darkBlack};
    outline-offset: 2px;
  }
`

/** Side (non-active) cars: clicking brings the car to the center. */
export const SlideButton = styled.button`
  ${slideTarget}
`

/** Active (centered) car: links to the model page, same destination as the CTA (DLK-1087). */
export const SlideLink = styled(NextLink)`
  ${slideTarget}
`

export const Section = styled.section`
  width: 100%;
  background: ${(p) => p.theme.colors.white};
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding-block: 64px;
  ${MOBILE} {
    gap: 24px;
    padding-block: 32px;
  }
`

export const SecHead = styled(HomePageContainer)`
  display: flex;
  flex-direction: column;
  align-items: flex-start; /* right edge in RTL */
  gap: 8px;
  text-align: right;
`

export const Eyebrow = styled.span`
  font-family: ${Font.MazdaType};
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 4px;
  color: ${(p) => p.theme.colors.newPrimaryRed};
`

export const Headline = styled.h2`
  margin: 0;
  max-width: 700px;
  font-family: ${Font.OENaPro};
  font-weight: 300;
  font-size: 40px;
  line-height: 64px;
  color: ${(p) => p.theme.colors.darkBlack};
  ${MOBILE} {
    font-size: 28px;
    line-height: 36px;
  }
`

/* Carousel area: full-bleed row with the swiper centered and arrows at the edges. */
export const CarouselArea = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`

export const ArrowSlot = styled.div<{ $side: "left" | "right" }>`
  position: absolute;
  top: 40%;
  ${(p) => (p.$side === "left" ? "left: 240px;" : "right: 240px;")}
  ${(p) => p.theme.breakpoints.down("smallLaptop")} {
    ${(p) => (p.$side === "left" ? "left: 64px;" : "right: 64px;")}
  }
  z-index: 2;
  ${MOBILE} {
    display: none; /* mobile is swipe-driven */
  }
`

export const Foot = styled(HomePageContainer)`
  display: flex;
  flex-direction: column;
  align-items: center;
`
